import { post } from './api';

export interface BillingPlan {
  id: number;
  plan_code: string;
  name: string;
  display_name?: string;
  description?: string;
  currency: string;
  monthly_price_cents: number;
  annual_price_cents: number;
  trial_days: number;
  daily_quota: number;
  allow_overage: boolean;
  overage_price_micros: number;
  features?: string[];
  status: 'active' | 'inactive';
  is_default: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface SubscriptionInfo {
  id: number;
  client_id: number;
  user_id: number;
  plan_id: number;
  plan_code: string;
  status: string;
  start_date: string;
  end_date?: string;
  trial_end_at?: string;
  auto_renew: boolean;
  daily_quota: number;
  allow_overage: boolean;
  overage_price_micros: number;
  metadata?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SubscriptionHistoryItem {
  id: number;
  subscription_id: number;
  client_id: number;
  old_plan_id?: number;
  old_plan_code?: string;
  new_plan_id?: number;
  new_plan_code?: string;
  change_type: string;
  change_reason?: string;
  changed_by?: number;
  metadata?: string;
  created_at: string;
}

export interface PaymentOrder {
  id: string;
  client_id: number;
  user_id: number;
  plan_id: number;
  billing_cycle: 'monthly' | 'yearly';
  amount: number;
  currency: string;
  status: string;
  np_invoice_id?: string;
  np_payment_id?: string;
  np_actually_paid?: number;
  invoice_url?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateOrderResponse {
  order_id: string;
  invoice_url: string;
  amount: number;
  currency: string;
  status: string;
}

export const billingService = {
  async listPlans(includeInactive = false) {
    const res = await post<{ plans: BillingPlan[] }>('/api/billing/plans/list', {
      include_inactive: includeInactive,
    });
    return res.data?.plans ?? [];
  },

  async createPlan(payload: Partial<BillingPlan>) {
    return post('/api/billing/plans/create', payload);
  },

  async updatePlan(payload: Partial<BillingPlan> & { id: number }) {
    return post('/api/billing/plans/update', payload);
  },

  async togglePlan(id: number, status: 'active' | 'inactive') {
    return post('/api/billing/plans/toggle', { id, status });
  },

  async getCurrentSubscription(clientId?: number) {
    const res = await post<{ subscription: SubscriptionInfo | null; plan?: BillingPlan }>(
      '/api/billing/subscriptions/current',
      clientId ? { client_id: clientId } : undefined,
    );
    return {
      subscription: res.data?.subscription ?? null,
      plan: res.data?.plan ?? null,
    };
  },

  async setSubscriptionPlan(params: { client_id?: number; plan_id?: number; plan_code?: string; change_type?: string; change_reason?: string }) {
    return post('/api/billing/subscriptions/set', params);
  },

  async setClientPlanForAdmin(params: { client_id: number; plan_id?: number; plan_code?: string; change_reason?: string }) {
    return post('/api/admin/clients/set-plan', params);
  },

  async getSubscriptionHistory(params?: { subscription_id?: number; client_id?: number; limit?: number }) {
    const res = await post<{ history: SubscriptionHistoryItem[] }>('/api/billing/subscriptions/history', params);
    return res.data?.history ?? [];
  },

  // 支付相关
  async createOrder(planId: number, billingCycle: 'monthly' | 'yearly') {
    const res = await post<CreateOrderResponse>('/api/payment/create', {
      plan_id: planId,
      billing_cycle: billingCycle,
    });
    return res.data!;
  },

  async getOrderStatus(orderId: string) {
    const res = await post<PaymentOrder>('/api/payment/status', { order_id: orderId });
    return res.data!;
  },

  async listOrders(page = 1, size = 20) {
    const res = await post<{ list: PaymentOrder[]; total: number }>('/api/payment/orders', { page, size });
    return res.data!;
  },
};

export type { BillingPlan as BillingPlanResponse, SubscriptionInfo as SubscriptionResponse };

